import {
  CaseType,
  ExternalNamedOffender,
  InternalNamedOffender,
  NamedOffender,
  ReportEventType,
  ReportType,
} from 'src/graphql';
import { getReadableIdWithDash } from 'src/types';
import { uniqueArray } from 'src/utils';
import { DELETION_DATE_ONLY_REGEX } from 'src/utils/commonRegex';
import {
  FormattedNamedOffender,
  NamedOffenderMap,
} from '../formattedNamedOffender.type';

export const addExternalNamedOffenders = (
  eventNamedOffenders: NamedOffender[],
  offendersWithReportsAndEvents: Map<string, NamedOffenderMap>,
) => {
  eventNamedOffenders
    .filter(
      (offender): offender is ExternalNamedOffender =>
        Boolean((offender as ExternalNamedOffender)?.name) &&
        Boolean((offender as ExternalNamedOffender)?.dataDeletedAt === null) &&
        !DELETION_DATE_ONLY_REGEX.test(
          (offender as ExternalNamedOffender)?.name.toLowerCase(),
        ),
    )
    .forEach((offender) => {
      const offenderId = String(offender.id);
      offendersWithReportsAndEvents.set(offenderId, {
        id: offender.id,
        name: offender?.name ?? '',
        reports: [getReadableIdWithDash(offender.reportId ?? '')],
        events: [getReadableIdWithDash(offender.event)],
        external: true,
        linkedOffenderId: null,
      });
    });
};

export const addInternalNamedOffenders = (
  eventNamedOffenders: NamedOffender[],
  offendersWithReportsAndEvents: Map<string, NamedOffenderMap>,
) => {
  eventNamedOffenders
    .filter(
      (offender): offender is InternalNamedOffender =>
        Boolean((offender as InternalNamedOffender)?.User) &&
        Boolean((offender as InternalNamedOffender)?.dataDeletedAt === null) &&
        (offender as InternalNamedOffender)?.User?.name?.toLowerCase() !==
          '[deleted]',
    )
    .forEach((offender) => {
      const offenderId = String(offender.User?.id);
      const existing = offendersWithReportsAndEvents.get(offenderId);
      offendersWithReportsAndEvents.set(offenderId, {
        id: offenderId,
        name: offender.User?.name ?? '',
        reports: uniqueArray([
          ...(existing ? existing.reports : []),
          getReadableIdWithDash(offender.reportId ?? ''),
        ]),
        events: uniqueArray([
          ...(existing ? existing.events : []),
          getReadableIdWithDash(offender.event),
        ]),
        external: false,
        linkedOffenderId: offender.linkedPresubmissionOffender,
        site: offender.User?.site ?? undefined,
        department: offender.User?.department ?? undefined,
        country: offender.User?.country ?? undefined,
      });
    });
};

export const getEventPostsubmissionNamedOffenders = (
  event: ReportEventType,
) => {
  const offendersWithReportsAndEvents = new Map<string, NamedOffenderMap>();
  const eventNamedOffenders = event.NamedOffenders.filter(
    (offender) => offender.event === event.id && offender.postSubmission,
  );

  addExternalNamedOffenders(eventNamedOffenders, offendersWithReportsAndEvents);
  addInternalNamedOffenders(eventNamedOffenders, offendersWithReportsAndEvents);

  const namedOffenders: FormattedNamedOffender[] = [];
  offendersWithReportsAndEvents.forEach((value, key) =>
    namedOffenders.push({
      id: key,
      reports: value.reports.map((id) => ({ id, type: 'Postsubmission' })),
      events: value.events.map((id) => ({ id, type: 'Postsubmission' })),
      name: value.name,
      external: value.external,
      site: value.site,
      department: value.department,
      country: value.country,
      linkedOffenderId: value.linkedOffenderId,
    }),
  );

  return namedOffenders;
};

export const getReportPostsubmissionNamedOffenders = (report: ReportType) => {
  const offendersWithReportsAndEvents = new Map<string, NamedOffenderMap>();
  const reportEvents = report.ReportEvents ?? [];

  const eventNamedOffenders = reportEvents.flatMap((event) =>
    event.NamedOffenders.filter(
      (offender) => offender.event === event.id && offender.postSubmission,
    ),
  );

  addExternalNamedOffenders(eventNamedOffenders, offendersWithReportsAndEvents);
  addInternalNamedOffenders(eventNamedOffenders, offendersWithReportsAndEvents);

  const namedOffenders: FormattedNamedOffender[] = [];
  offendersWithReportsAndEvents.forEach((value, key) =>
    namedOffenders.push({
      id: key,
      reports: value.reports.map((id) => ({ id, type: 'Postsubmission' })),
      events: value.events.map((id) => ({ id, type: 'Postsubmission' })),
      name: value.name,
      external: value.external,
      site: value.site,
      department: value.department,
      country: value.country,
      linkedOffenderId: value.linkedOffenderId,
    }),
  );

  return namedOffenders;
};

export const getCasePostsubmissionNamedOffenders = (caseInstance: CaseType) => {
  const offendersWithReportsAndEvents = new Map<string, NamedOffenderMap>();

  const reports = caseInstance.Reports;

  const reportEvents =
    reports?.flatMap((report) => report.ReportEvents ?? []) ?? [];

  const eventNamedOffenders = reportEvents.flatMap((event) =>
    event.NamedOffenders.filter(
      (offender) => offender.event === event.id && offender.postSubmission,
    ),
  );

  addExternalNamedOffenders(eventNamedOffenders, offendersWithReportsAndEvents);
  addInternalNamedOffenders(eventNamedOffenders, offendersWithReportsAndEvents);

  const namedOffenders: FormattedNamedOffender[] = [];
  offendersWithReportsAndEvents.forEach((value) =>
    namedOffenders.push({
      id: value.id,
      reports: value.reports.map((id) => ({ id, type: 'Postsubmission' })),
      events: value.events.map((id) => ({ id, type: 'Postsubmission' })),
      name: value.name,
      external: value.external,
      site: value.site,
      department: value.department,
      country: value.country,
      linkedOffenderId: value.linkedOffenderId,
    }),
  );

  return namedOffenders;
};
